import { Box, Paper } from '@mui/material'
import React from 'react'

type TwoColumnLayoutProps = {
  children: React.ReactNode
  /**
   * Order page only: keep the details shell height-stable so form/tracking
   * columns can stretch. Other pages keep the default scrollable shell.
   */
  lockDetailsOverflow?: boolean
}

/** Fills the main outlet column; scroll stays inside each panel (no chaining to siblings / window). */
const TwoColumnLayout = ({
  children,
  lockDetailsOverflow = false,
}: TwoColumnLayoutProps) => {
  const childrenArray = React.Children.toArray(children)

  const paperSx = {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    borderRadius: { xs: 1, sm: 0 },
    boxShadow: { xs: 3, sm: 2 },
  }

  /** Single scrollport per column — prevents nested viewport hacks fighting this container. */
  const scrollBodySx = {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehaviorY: 'contain',
    overscrollBehaviorX: 'none',
    WebkitOverflowScrolling: 'touch',
    display: 'flex',
    flexDirection: 'column',
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        width: '100%',
        flexDirection: 'row',
        gap: 0,
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          flex: { sm: '0 0 33.333%', lg: '0 0 25%' },
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <Paper
          elevation={4}
          sx={{
            ...paperSx,
            px: 2,
            pb: 0,
            pt: 2,
            backgroundColor: 'white',
          }}
          className="hide-scrollbar"
        >
          <Box sx={scrollBodySx}>{childrenArray[0]}</Box>
        </Paper>
      </Box>
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          flex: '1 1 0%',
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <Paper elevation={4} sx={paperSx} className="hide-scrollbar">
          <Box
            sx={{
              ...scrollBodySx,
              ...(lockDetailsOverflow ? { overflow: 'hidden' } : {}),
            }}
          >
            {childrenArray[1]}
          </Box>
        </Paper>
      </Box>
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <Paper elevation={4} sx={paperSx} className="hide-scrollbar">
          <Box sx={scrollBodySx}>{childrenArray[2]}</Box>
        </Paper>
      </Box>
    </Box>
  )
}

export default TwoColumnLayout
